/**
 * Settings store.
 *
 * Strategy:
 *   - Single module-level cache for the current session.
 *   - localStorage for persistence across reloads.
 *   - getSettings() always returns a valid AppSettings (never null).
 *   - updateSettings() accepts partials and merges immutably.
 *   - No React context/provider: pages call getSettings() directly.
 *     When React re-renders are needed from settings changes, the
 *     SettingsPage already calls useState, which triggers re-render.
 *     Other pages read settings on-demand (at request time), so they
 *     always see the latest values without subscriptions.
 *
 * Validation:
 *   - On load, stored data is validated against known providers and
 *     sanitized so that corrupted localStorage never crashes the app.
 *   - Empty strings for required fields fall back to defaults.
 */

import type { AppSettings, AIProvider, FormulaLocale } from '@excel-ai-studio/shared';
import { DEFAULT_SETTINGS, DEFAULT_MODELS, PROVIDER_REQUIRES_KEY } from '@excel-ai-studio/shared';

const STORAGE_KEY = 'excel-ai-studio-settings';

/** All valid provider keys */
const VALID_PROVIDERS: ReadonlySet<string> = new Set<AIProvider>([
  'openai', 'anthropic', 'deepseek', 'ollama',
]);

/** All valid formula locale keys */
const VALID_FORMULA_LOCALES: ReadonlySet<string> = new Set<FormulaLocale>([
  'auto', 'fr', 'en',
]);

let cached: AppSettings | null = null;

// ─── Validation ──────────────────────────────────────────

/**
 * Sanitize a raw parsed object into a valid AppSettings.
 * Any invalid or missing field is replaced by the default.
 */
function sanitize(raw: Record<string, unknown>): AppSettings {
  const provider: AIProvider = VALID_PROVIDERS.has(raw.provider as string)
    ? (raw.provider as AIProvider)
    : DEFAULT_SETTINGS.provider;

  const models = DEFAULT_MODELS[provider];
  const rawModel = typeof raw.model === 'string' && raw.model.trim() !== '' ? raw.model.trim() : '';
  const model = rawModel || models[0];

  const apiKey = typeof raw.apiKey === 'string' ? raw.apiKey : '';
  const baseUrl = typeof raw.baseUrl === 'string' && raw.baseUrl.trim() !== ''
    ? raw.baseUrl.trim()
    : undefined;

  const language = raw.language === 'en' || raw.language === 'fr'
    ? raw.language
    : DEFAULT_SETTINGS.language;

  const formulaLocale: FormulaLocale = VALID_FORMULA_LOCALES.has(raw.formulaLocale as string)
    ? (raw.formulaLocale as FormulaLocale)
    : DEFAULT_SETTINGS.formulaLocale;

  const theme = raw.theme === 'light' || raw.theme === 'dark' || raw.theme === 'auto'
    ? raw.theme
    : DEFAULT_SETTINGS.theme;

  const copilotExplainabilityPanel =
    typeof raw.copilotExplainabilityPanel === 'boolean'
      ? raw.copilotExplainabilityPanel
      : DEFAULT_SETTINGS.copilotExplainabilityPanel;
  const copilotRequireWriteConfirmation =
    typeof raw.copilotRequireWriteConfirmation === 'boolean'
      ? raw.copilotRequireWriteConfirmation
      : DEFAULT_SETTINGS.copilotRequireWriteConfirmation;
  const copilotAutoExecuteCreate =
    typeof raw.copilotAutoExecuteCreate === 'boolean'
      ? raw.copilotAutoExecuteCreate
      : DEFAULT_SETTINGS.copilotAutoExecuteCreate;
  const copilotContextLevel =
    raw.copilotContextLevel === 'minimal' ||
    raw.copilotContextLevel === 'activeSheet' ||
    raw.copilotContextLevel === 'fullWorkbook'
      ? raw.copilotContextLevel
      : DEFAULT_SETTINGS.copilotContextLevel;
  const copilotEnableStrictGuardrails =
    typeof raw.copilotEnableStrictGuardrails === 'boolean'
      ? raw.copilotEnableStrictGuardrails
      : DEFAULT_SETTINGS.copilotEnableStrictGuardrails;
  const copilotEnableAdvancedPivot =
    typeof raw.copilotEnableAdvancedPivot === 'boolean'
      ? raw.copilotEnableAdvancedPivot
      : DEFAULT_SETTINGS.copilotEnableAdvancedPivot;
  const copilotEnableMetrics =
    typeof raw.copilotEnableMetrics === 'boolean'
      ? raw.copilotEnableMetrics
      : DEFAULT_SETTINGS.copilotEnableMetrics;
  const copilotDebugMode =
    typeof raw.copilotDebugMode === 'boolean'
      ? raw.copilotDebugMode
      : DEFAULT_SETTINGS.copilotDebugMode;

  return {
    provider,
    model,
    apiKey,
    baseUrl,
    language,
    formulaLocale,
    theme,
    copilotExplainabilityPanel,
    copilotRequireWriteConfirmation,
    copilotAutoExecuteCreate,
    copilotContextLevel,
    copilotEnableStrictGuardrails,
    copilotEnableAdvancedPivot,
    copilotEnableMetrics,
    copilotDebugMode,
  };
}

// ─── Public API ──────────────────────────────────────────

/** Get current settings. Always returns a valid object. */
export function getSettings(): AppSettings {
  if (cached) return cached;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      cached = sanitize(parsed);
      return cached;
    }
  } catch {
    // Corrupted data — reset silently
  }

  cached = { ...DEFAULT_SETTINGS };
  return cached;
}

/** Update settings. Merges partial into current, persists, returns new state. */
export function updateSettings(partial: Partial<AppSettings>): AppSettings {
  const merged = { ...getSettings(), ...partial };
  // Re-sanitize to ensure invariants hold after merge
  const updated = sanitize(merged as unknown as Record<string, unknown>);
  cached = updated;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

/** Reset all settings to defaults. */
export function resetSettings(): AppSettings {
  cached = { ...DEFAULT_SETTINGS };
  localStorage.removeItem(STORAGE_KEY);
  return cached;
}

// ─── Convenience getters ─────────────────────────────────

export function getApiKey(): string {
  return getSettings().apiKey;
}

export function getProvider(): AIProvider {
  return getSettings().provider;
}

export function getModel(): string {
  return getSettings().model;
}

/**
 * Check if the current configuration is ready for AI calls.
 * - Provider that requires a key: apiKey must be non-empty.
 * - Ollama: always ready (no key needed).
 */
export function isSettingsReady(): boolean {
  const s = getSettings();
  if (PROVIDER_REQUIRES_KEY[s.provider]) {
    return s.apiKey.trim() !== '';
  }
  return true;
}
